import { useState, useMemo, useRef } from 'react'
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts'
import { useFinance } from '../../context/FinanceContext'
import { useSettings } from '../../context/SettingsContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatCurrencyExact } from '../../lib/formatters'
import {
  compareRothVsTraditional,
  CONTRIBUTION_LIMITS_2025,
  ROTH_IRA_PHASEOUT,
  STANDARD_DEDUCTIONS,
  STATE_TAX_DATA,
} from '../../lib/calculations'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'

const FILING_STATUSES = [
  { key: 'single', label: 'Single' },
  { key: 'mfj', label: 'Married Filing Jointly' },
  { key: 'hoh', label: 'Head of Household' },
]

const MATCH_OPTIONS = [
  { key: 'both', label: 'Both Roth & Traditional 401(k)' },
  { key: 'traditional', label: 'Traditional 401(k) Only' },
  { key: 'roth', label: 'Roth 401(k) Only' },
]

const INCOME_PRESETS = [
  { label: 'Early Career', income: 45000, desc: '$45k - 12% bracket' },
  { label: 'Mid Career', income: 85000, desc: '$85k - 22% bracket' },
  { label: 'High Earner', income: 180000, desc: '$180k - 24%+ bracket' },
  { label: 'Dual Income', income: 150000, desc: '$150k MFJ - 22% bracket' },
]

export default function RothVsTraditional() {
  const { state } = useFinance()
  const { settings } = useSettings()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const projectionChartRef = useRef(null)
  const breakEvenChartRef = useRef(null)
  const rd = settings.calculatorDefaults.rothVsTraditional

  const [filingStatus, setFilingStatus] = useState(rd.filingStatus)
  const [grossIncome, setGrossIncome] = useState(rd.grossIncome)
  const [currentAge, setCurrentAge] = useState(rd.currentAge)
  const [retirementAge, setRetirementAge] = useState(rd.retirementAge)
  const [annualContribution, setAnnualContribution] = useState(rd.annualContribution)
  const [expectedReturn, setExpectedReturn] = useState(rd.expectedReturn)
  const [expectedRetirementIncome, setExpectedRetirementIncome] = useState(50000)
  const [selectedState, setSelectedState] = useState(rd.selectedState)
  const [customStateTaxRate, setCustomStateTaxRate] = useState(0)
  const [employerMatch, setEmployerMatch] = useState(3)
  const [matchAppliesTo, setMatchAppliesTo] = useState('both')
  const [startingRothBalance, setStartingRothBalance] = useState(0)
  const [startingTradBalance, setStartingTradBalance] = useState(0)
  const [showProjectionTable, setShowProjectionTable] = useState(false)

  // Derive state tax rate from selection
  const stateData = STATE_TAX_DATA.find(s => s.code === selectedState)
  const stateTaxRate = selectedState === 'CUSTOM' ? customStateTaxRate
    : selectedState === 'NONE' ? 0
    : (stateData?.rate ?? 0)

  const result = useMemo(() =>
    compareRothVsTraditional({
      grossIncome, filingStatus, currentAge, retirementAge,
      annualContribution, expectedReturn, expectedRetirementIncome,
      stateTaxRate, employerMatch, matchAppliesTo,
      startingRothBalance, startingTradBalance,
    }),
    [grossIncome, filingStatus, currentAge, retirementAge, annualContribution, expectedReturn, expectedRetirementIncome, stateTaxRate, employerMatch, matchAppliesTo, startingRothBalance, startingTradBalance]
  )

  const handlePreset = (income) => {
    setGrossIncome(income)
  }

  // PDF Export
  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const margin = 14
    let y

    const matchLabel = MATCH_OPTIONS.find(o => o.key === matchAppliesTo)?.label || matchAppliesTo
    const filingLabel = FILING_STATUSES.find(f => f.key === filingStatus)?.label || filingStatus
    const rec = result.recommendation
    const confidenceLabel = rec.confidence === 'high' ? 'High' : 'Moderate'

    // ── Title ──
    doc.setFontSize(22)
    doc.setTextColor(30, 41, 59)
    doc.text('Roth vs Traditional Analysis', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(100, 116, 139)
    doc.text(`Generated by Liquid Finance  •  ${new Date().toLocaleDateString()}`, margin, 30)

    // ── Your Details (Inputs) ──
    doc.setFontSize(14)
    doc.setTextColor(30, 41, 59)
    doc.text('Your Details', margin, 42)

    const detailRows = [
      ['Filing Status', filingLabel],
      ['Gross Annual Income', formatCurrency(grossIncome)],
      ['Current Age', `${currentAge} years`],
      ['Retirement Age', `${retirementAge} years`],
      ['Your Annual Contribution', formatCurrency(annualContribution)],
      ['Expected Annual Return', `${expectedReturn}%`],
      ['Expected Retirement Income', formatCurrency(expectedRetirementIncome)],
      ['State', `${stateData?.name || 'Custom'} (${stateTaxRate}%)`],
    ]
    if (employerMatch > 0) {
      detailRows.push(['Employer Match', `${employerMatch}% of salary`])
      detailRows.push(['Match Applies To', matchLabel])
      detailRows.push(['Employer Match Amount', `${formatCurrency(result.annualEmployerMatch)}/yr`])
      detailRows.push(['Total Annual (You + Match)', formatCurrency(result.totalAnnualWithMatch)])
    }
    if (startingRothBalance > 0) {
      detailRows.push(['Starting Roth Balance', formatCurrency(startingRothBalance)])
    }
    if (startingTradBalance > 0) {
      detailRows.push(['Starting Traditional Balance', formatCurrency(startingTradBalance)])
    }

    autoTable(doc, {
      head: [['Parameter', 'Value']],
      body: detailRows,
      startY: 46,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      margin: { left: margin, right: margin },
    })

    // ── Tax Bracket Summary (4 result cards) ──
    y = (doc.lastAutoTable?.finalY || 80) + 10
    doc.setFontSize(14)
    doc.setTextColor(30, 41, 59)
    doc.text('Current Tax Position', margin, y)

    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: [
        ['Marginal Tax Rate', `${result.currentMarginalRate}%`],
        ['Effective Tax Rate', `${result.currentEffectiveRate}%`],
        ['Federal Tax', formatCurrency(result.currentFederalTax)],
        ['Taxable Income', formatCurrency(result.taxableIncome)],
      ],
      startY: y + 4,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10 },
      margin: { left: margin, right: margin },
      columnStyles: { 1: { fontStyle: 'bold' } },
    })

    // ── Tax Bracket Breakdown ──
    if (result.bracketBreakdown.length > 0) {
      y = (doc.lastAutoTable?.finalY || 80) + 10
      doc.setFontSize(14)
      doc.setTextColor(30, 41, 59)
      doc.text('Tax Bracket Breakdown', margin, y)

      autoTable(doc, {
        head: [['Bracket', 'Taxable Amount', 'Tax Owed', '% of Income']],
        body: result.bracketBreakdown.map(b => [
          `${b.rate}%`,
          formatCurrency(b.taxableAmount),
          formatCurrency(b.tax),
          `${b.fillPercent.toFixed(1)}%`,
        ]),
        startY: y + 4,
        theme: 'grid',
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9 },
        margin: { left: margin, right: margin },
      })
    }

    // ── Recommendation ──
    doc.addPage()
    doc.setFontSize(16)
    doc.setTextColor(30, 41, 59)
    doc.text('Recommendation', margin, 22)

    y = 30
    doc.setFontSize(12)
    doc.setTextColor(30, 41, 59)
    doc.text(`Strategy: ${rec.strategy}`, margin, y)
    y += 6
    doc.setFontSize(10)
    doc.setTextColor(100, 116, 139)
    doc.text(`Confidence: ${confidenceLabel}   |   Next Step: ${rec.nextStep}`, margin, y)

    // Reasons
    y += 10
    doc.setFontSize(11)
    doc.setTextColor(30, 41, 59)
    doc.text('Reasoning:', margin, y)
    y += 6
    doc.setFontSize(9)
    doc.setTextColor(51, 65, 85)
    rec.reasons.forEach(reason => {
      const lines = doc.splitTextToSize(`• ${reason}`, doc.internal.pageSize.getWidth() - margin * 2)
      doc.text(lines, margin + 2, y)
      y += lines.length * 5
    })

    // Roth Advantages
    y += 6
    doc.setFontSize(11)
    doc.setTextColor(30, 41, 59)
    doc.text('Roth Advantages:', margin, y)
    y += 6
    doc.setFontSize(9)
    doc.setTextColor(51, 65, 85)
    rec.rothAdvantages.forEach(adv => {
      const lines = doc.splitTextToSize(`- ${adv}`, doc.internal.pageSize.getWidth() - margin * 2)
      doc.text(lines, margin + 2, y)
      y += lines.length * 5
    })

    // Traditional Advantages
    y += 6
    doc.setFontSize(11)
    doc.setTextColor(30, 41, 59)
    doc.text('Traditional Advantages:', margin, y)
    y += 6
    doc.setFontSize(9)
    doc.setTextColor(51, 65, 85)
    rec.tradAdvantages.forEach(adv => {
      const lines = doc.splitTextToSize(`- ${adv}`, doc.internal.pageSize.getWidth() - margin * 2)
      doc.text(lines, margin + 2, y)
      y += lines.length * 5
    })

    // ── Beyond the Match Strategy ──
    if (result.beyondMatch && result.beyondMatch.steps.length > 0) {
      y += 10
      if (y > doc.internal.pageSize.getHeight() - 60) {
        doc.addPage()
        y = 22
      }
      doc.setFontSize(14)
      doc.setTextColor(30, 41, 59)
      doc.text('Beyond the Match: Where Should Your Next Dollar Go?', margin, y)
      y += 6
      doc.setFontSize(9)
      doc.setTextColor(100, 116, 139)
      const summaryLines = doc.splitTextToSize(result.beyondMatch.summary, doc.internal.pageSize.getWidth() - margin * 2)
      doc.text(summaryLines, margin, y)
      y += summaryLines.length * 5 + 4

      autoTable(doc, {
        head: [['Step', 'Action', 'Account', 'Amount', 'Next Step', 'Rationale']],
        body: result.beyondMatch.steps.map(step => [
          step.order,
          step.action,
          step.account,
          step.amount != null ? `${formatCurrency(step.amount)}/yr` : '—',
          step.nextStep,
          step.rationale,
        ]),
        startY: y,
        theme: 'grid',
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2 },
        margin: { left: margin, right: margin },
        columnStyles: { 5: { cellWidth: 50 } },
      })
    }

    // ── Side-by-Side Results ──
    doc.addPage()
    doc.setFontSize(16)
    doc.setTextColor(30, 41, 59)
    doc.text('Side-by-Side Comparison Results', margin, 22)

    const resultsBody = [
      ['Roth After-Tax Value', formatCurrency(result.finalRothAfterTax)],
      ['Traditional After-Tax Value', formatCurrency(result.finalTradAfterTax)],
      [`Winner: ${result.winner === 'roth' ? 'Roth' : 'Traditional'}`, `+${formatCurrency(result.advantageAmount)}`],
      ['Annual Tax Savings (Traditional)', formatCurrency(result.annualTaxSavings)],
      ['Break-Even Retirement Rate', result.breakEvenRate != null ? `${result.breakEvenRate}%` : 'N/A'],
      ['Retirement Effective Rate', `${result.retirementEffectiveRate}%`],
    ]
    if (result.annualEmployerMatch > 0) {
      resultsBody.push([`Employer Match (${result.employerMatchRate}%)`, `${formatCurrency(result.annualEmployerMatch)}/yr`])
    }

    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: resultsBody,
      startY: 28,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10 },
      margin: { left: margin, right: margin },
      columnStyles: { 1: { fontStyle: 'bold' } },
    })

    // Employer match note
    if (result.annualEmployerMatch > 0) {
      y = (doc.lastAutoTable?.finalY || 60) + 6
      doc.setFontSize(9)
      doc.setTextColor(100, 116, 139)
      const matchNote = `Employer match note: Your employer contributes ${formatCurrency(result.annualEmployerMatch)}/yr (${result.employerMatchRate}% of salary) on top of your ${formatCurrency(annualContribution)}/yr. This match is always pre-tax — even with a Roth 401(k), the employer portion goes into a Traditional pre-tax account and is taxed at withdrawal. Both projections above include the match.`
      const matchLines = doc.splitTextToSize(matchNote, doc.internal.pageSize.getWidth() - margin * 2)
      doc.text(matchLines, margin, y)
    }

    // ── Growth Projection Chart ──
    if (result.yearlyProjection.length > 0) {
      await addChartToPDF(doc, projectionChartRef, { title: 'Growth Projection' })
    }

    // ── Break-Even Chart ──
    if (result.breakEvenData.length > 0) {
      await addChartToPDF(doc, breakEvenChartRef, { title: 'Break-Even Analysis' })
    }

    // ── 2025 Contribution Limits Reference ──
    doc.addPage()
    doc.setFontSize(16)
    doc.setTextColor(30, 41, 59)
    doc.text('2025 Contribution Limits Reference', margin, 22)

    const limitsBody = [
      ['401(k)', formatCurrency(CONTRIBUTION_LIMITS_2025.traditional401k), `${formatCurrency(CONTRIBUTION_LIMITS_2025.traditional401kCatchUp)} if 50+`],
      ['Roth IRA', formatCurrency(CONTRIBUTION_LIMITS_2025.rothIRA), `${formatCurrency(CONTRIBUTION_LIMITS_2025.rothIRACatchUp)} if 50+`],
      ['HSA (Individual)', formatCurrency(CONTRIBUTION_LIMITS_2025.hsaIndividual), '—'],
      ['HSA (Family)', formatCurrency(CONTRIBUTION_LIMITS_2025.hsaFamily), '—'],
    ]

    autoTable(doc, {
      head: [['Account', 'Limit', 'Catch-Up']],
      body: limitsBody,
      startY: 28,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10 },
      margin: { left: margin, right: margin },
    })

    // Roth IRA eligibility
    y = (doc.lastAutoTable?.finalY || 60) + 8
    doc.setFontSize(11)
    doc.setTextColor(30, 41, 59)
    doc.text('Roth IRA Eligibility', margin, y)
    y += 6
    doc.setFontSize(9)
    doc.setTextColor(51, 65, 85)
    const eligStatus = result.rothIRAEligibility.status === 'full' ? 'Fully Eligible'
      : result.rothIRAEligibility.status === 'partial' ? `Partial (max ${formatCurrency(result.rothIRAEligibility.maxContribution)})`
      : 'Phased Out'
    doc.text(`Status: ${eligStatus}`, margin, y)
    y += 5
    if (result.rothIRAEligibility.note) {
      const noteLines = doc.splitTextToSize(result.rothIRAEligibility.note, doc.internal.pageSize.getWidth() - margin * 2)
      doc.text(noteLines, margin, y)
      y += noteLines.length * 5
    }
    y += 4
    doc.text(`Roth IRA MAGI Phase-out: ${formatCurrency(ROTH_IRA_PHASEOUT[filingStatus].start)} - ${formatCurrency(ROTH_IRA_PHASEOUT[filingStatus].end)} (${filingLabel})`, margin, y)
    y += 5
    doc.text(`Standard Deduction: ${formatCurrency(STANDARD_DEDUCTIONS[filingStatus])}`, margin, y)

    // ── Year-by-Year Projection Table (ALL rows) ──
    if (result.yearlyProjection.length > 0) {
      doc.addPage()
      doc.setFontSize(16)
      doc.setTextColor(30, 41, 59)
      doc.text('Year-by-Year Projection', margin, 22)

      const projHead = result.annualEmployerMatch > 0
        ? [['Year', 'Age', 'Roth Balance', 'Match (Pre-Tax)', 'Trad Pre-Tax', 'Trad Brokerage', 'Roth After-Tax', 'Trad After-Tax', 'Advantage']]
        : [['Year', 'Age', 'Roth Balance', 'Trad Pre-Tax', 'Trad Brokerage', 'Roth After-Tax', 'Trad After-Tax', 'Advantage']]

      const projBody = result.yearlyProjection.map(r => {
        const row = [
          r.year,
          r.age,
          formatCurrency(r.rothBalance),
        ]
        if (result.annualEmployerMatch > 0) {
          row.push(formatCurrency(r.rothEmployerBalance))
        }
        row.push(
          formatCurrency(r.tradPreTaxBalance),
          formatCurrency(r.tradBrokerageBalance),
          formatCurrency(r.rothAfterTax),
          formatCurrency(r.tradAfterTax),
          `${r.advantage >= 0 ? '+' : ''}${formatCurrency(r.advantage)}`,
        )
        return row
      })

      autoTable(doc, {
        head: projHead,
        body: projBody,
        startY: 28,
        theme: 'striped',
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 7 },
        margin: { left: margin, right: margin },
        didParseCell: (data) => {
          if (data.section === 'body' && data.row.index % 5 === 4) {
            data.cell.styles.fillColor = [241, 245, 249]
          }
        },
      })
    }

    doc.save(`roth-vs-traditional-${Date.now()}.pdf`)
  }

  const rec = result.recommendation
  const confidenceColor = rec.confidence === 'high' ? colors.positive : colors.warning

  return (
    <div className="space-y-6">
      {/* Filing Status + Export */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-semibold text-body mb-3">Filing Status</h3>
            <div className="flex flex-wrap gap-2">
              {FILING_STATUSES.map(fs => (
                <button
                  key={fs.key}
                  onClick={() => setFilingStatus(fs.key)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    filingStatus === fs.key
                      ? 'text-heading shadow-sm'
                      : 'text-muted hover:text-heading bg-hover'
                  }`}
                  style={filingStatus === fs.key ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : {}}
                >
                  {fs.label}
                </button>
              ))}
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
      </GlassCard>

      {/* Income Presets */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-3">Quick Start</h3>
        <div className="flex flex-wrap gap-2">
          {INCOME_PRESETS.map(p => (
            <button
              key={p.income}
              onClick={() => handlePreset(p.income)}
              className={`flex flex-col px-4 py-2.5 rounded-xl text-sm transition-all ${
                grossIncome === p.income
                  ? 'text-heading shadow-sm'
                  : 'text-muted hover:text-heading bg-hover'
              }`}
              style={grossIncome === p.income ? {
                background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                border: `1px solid ${colors.primary}44`,
              } : {}}
            >
              <span className="font-medium">{p.label}</span>
              <span className="text-xs text-muted">{p.desc}</span>
            </button>
          ))}
        </div>
      </GlassCard>

      {/* Inputs */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-4">Your Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField label="Gross Annual Income" value={grossIncome} onChange={setGrossIncome} prefix="$" type="number" min={0} />
          <InputField label="Current Age" value={currentAge} onChange={setCurrentAge} suffix="years" type="number" min={18} max={70} />
          <InputField label="Retirement Age" value={retirementAge} onChange={setRetirementAge} suffix="years" type="number" min={currentAge + 1} max={80} />
          <InputField label="Your Annual Contribution" value={annualContribution} onChange={setAnnualContribution} prefix="$" type="number" min={0} hint="Your employee contribution only (match added automatically)" />
          <InputField label="Expected Annual Return" value={expectedReturn} onChange={setExpectedReturn} suffix="%" type="number" step={0.1} min={0} max={15} />
          <InputField label="Expected Retirement Income" value={expectedRetirementIncome} onChange={setExpectedRetirementIncome} prefix="$" type="number" min={0} />
          <div>
            <label className="block text-xs font-medium text-body mb-1.5">State</label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:ring-1 appearance-none"
            >
              {STATE_TAX_DATA.map(s => (
                <option key={s.code} value={s.code}>
                  {s.code === 'NONE' ? 'No State Tax' : s.code === 'CUSTOM' ? 'Custom Rate...' : `${s.name} (${s.rate}%)`}
                </option>
              ))}
            </select>
            {selectedState === 'CUSTOM' ? (
              <div className="mt-1.5">
                <InputField label="" value={customStateTaxRate} onChange={setCustomStateTaxRate} suffix="%" type="number" step={0.1} min={0} max={15} />
              </div>
            ) : (
              <p className="text-xs text-muted mt-1">
                {stateTaxRate === 0 ? 'No state income tax' : `${stateTaxRate}% top marginal rate`}
              </p>
            )}
          </div>
          <InputField label="Employer Match" value={employerMatch} onChange={setEmployerMatch} suffix="% of salary" type="number" step={0.5} min={0} max={20} />
          <InputField label="Current Roth Balance" value={startingRothBalance} onChange={setStartingRothBalance} prefix="$" type="number" min={0} hint="Existing Roth 401k / Roth IRA balance" />
          <InputField label="Current Traditional Balance" value={startingTradBalance} onChange={setStartingTradBalance} prefix="$" type="number" min={0} hint="Existing Traditional 401k / IRA balance" />
        </div>

        {/* Employer Match Target */}
        {employerMatch > 0 && (
          <div className="mt-4">
            <label className="block text-xs font-medium text-body mb-2">Employer matches contributions to:</label>
            <div className="flex flex-wrap gap-2">
              {MATCH_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setMatchAppliesTo(opt.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    matchAppliesTo === opt.key
                      ? 'text-heading shadow-sm'
                      : 'text-muted hover:text-heading bg-hover'
                  }`}
                  style={matchAppliesTo === opt.key ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted mt-1">
              {matchAppliesTo === 'both' && 'Most common — employer matches whether you contribute to Roth or Traditional 401(k)'}
              {matchAppliesTo === 'traditional' && 'Employer only matches Traditional 401(k) contributions — you must contribute Traditional to get the match'}
              {matchAppliesTo === 'roth' && 'Rare — employer only matches Roth 401(k) contributions'}
            </p>
          </div>
        )}

        {/* Contribution Breakdown */}
        {result.annualEmployerMatch > 0 && (
          <div className="mt-3 p-3 rounded-xl bg-subtle flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <span className="text-body">Your contribution:</span>
            <span className="font-semibold text-heading">{formatCurrency(annualContribution)}/yr</span>
            <span className="text-body">+</span>
            <span className="text-body">Employer match ({employerMatch}%):</span>
            <span className="font-semibold text-positive">{formatCurrency(result.annualEmployerMatch)}/yr</span>
            <span className="text-body">=</span>
            <span className="text-body">Total:</span>
            <span className="font-bold text-heading">{formatCurrency(result.totalAnnualWithMatch)}/yr</span>
          </div>
        )}

        {/* Starting Balances Note */}
        {(startingRothBalance > 0 || startingTradBalance > 0) && (
          <div className="mt-2 text-xs text-body">
            Starting with {startingRothBalance > 0 && <><span className="font-semibold text-heading">{formatCurrency(startingRothBalance)}</span> in Roth</>}
            {startingRothBalance > 0 && startingTradBalance > 0 && ' + '}
            {startingTradBalance > 0 && <><span className="font-semibold text-heading">{formatCurrency(startingTradBalance)}</span> in Traditional</>}
          </div>
        )}
      </GlassCard>

      {/* Tax Bracket Display */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ResultCard label="Marginal Tax Rate" value={`${result.currentMarginalRate}%`} color={colors.primary} highlight />
        <ResultCard label="Effective Tax Rate" value={`${result.currentEffectiveRate}%`} color={colors.secondary} />
        <ResultCard label="Federal Tax" value={formatCurrency(result.currentFederalTax)} color={colors.negative} />
        <ResultCard label="Taxable Income" value={formatCurrency(result.taxableIncome)} color={colors.chartTick} />
      </div>

      {/* Bracket Visualization */}
      {result.bracketBreakdown.length > 0 && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-3">Tax Bracket Breakdown</h3>
          <div className="flex rounded-xl overflow-hidden h-8">
            {result.bracketBreakdown.map((b, i) => (
              <div
                key={b.rate}
                className="flex items-center justify-center text-xs font-bold transition-all"
                style={{
                  width: `${b.fillPercent}%`,
                  minWidth: b.fillPercent > 0 ? '30px' : 0,
                  backgroundColor: getBracketColor(b.rate),
                  color: b.rate >= 32 ? '#fef2f2' : '#ffffff',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }}
                title={`${b.rate}% bracket: ${formatCurrency(b.taxableAmount)} taxed = ${formatCurrency(b.tax)}`}
              >
                {b.fillPercent >= 8 && `${b.rate}%`}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-3">
            {result.bracketBreakdown.map(b => (
              <div key={b.rate} className="flex items-center gap-1.5 text-xs text-body">
                <span className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: getBracketColor(b.rate) }} />
                <span>{b.rate}%: {formatCurrency(b.taxableAmount)} ({formatCurrency(b.tax)} tax)</span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Recommendation */}
      <GlassCard>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <h3 className="text-sm font-semibold text-body">Recommendation</h3>
          <Badge variant={rec.confidence === 'high' ? 'success' : 'warning'}>
            {rec.strategy}
          </Badge>
          <span className="text-xs px-2 py-1 rounded-lg" style={{ background: confidenceColor + '22', color: confidenceColor }}>
            {rec.confidence} confidence
          </span>
          <span className="text-xs text-body">{rec.nextStep}</span>
        </div>

        <div className="space-y-2 mb-4">
          {rec.reasons.map((reason, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-body">
              <span className="mt-0.5 text-primary">&#8226;</span>
              <span>{reason}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-subtle">
            <h4 className="text-xs font-semibold text-heading mb-2 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.positive }} />
              Roth Advantages
            </h4>
            {rec.rothAdvantages.map((adv, i) => (
              <p key={i} className="text-xs text-body leading-relaxed">- {adv}</p>
            ))}
          </div>
          <div className="p-4 rounded-xl bg-subtle">
            <h4 className="text-xs font-semibold text-heading mb-2 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.secondary }} />
              Traditional Advantages
            </h4>
            {rec.tradAdvantages.map((adv, i) => (
              <p key={i} className="text-xs text-body leading-relaxed">- {adv}</p>
            ))}
          </div>
        </div>
      </GlassCard>

      {/* Beyond the Match Strategy */}
      {result.beyondMatch && result.beyondMatch.steps.length > 0 && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-2">Beyond the Match: Where Should Your Next Dollar Go?</h3>
          <p className="text-xs text-body mb-4">{result.beyondMatch.summary}</p>
          <div className="space-y-3">
            {result.beyondMatch.steps.map((step) => (
              <div key={step.order} className="flex gap-3 items-start">
                <div
                  className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: step.order === 1 ? colors.positive : step.order === 2 ? colors.primary : colors.secondary }}
                >
                  {step.order}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-heading">{step.action}</p>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-subtle text-body">{step.nextStep}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-subtle text-body">{step.account}</span>
                    {step.amount != null && (
                      <span className="text-xs font-medium" style={{ color: colors.positive }}>{formatCurrency(step.amount)}/yr</span>
                    )}
                  </div>
                  <p className="text-xs text-muted mt-0.5">{step.rationale}</p>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Side-by-Side Results */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <ResultCard label="Roth After-Tax Value" value={formatCurrency(result.finalRothAfterTax)} color={colors.positive} highlight />
        <ResultCard label="Traditional After-Tax Value" value={formatCurrency(result.finalTradAfterTax)} color={colors.secondary} highlight />
        <ResultCard
          label={`Winner: ${result.winner === 'roth' ? 'Roth' : 'Traditional'}`}
          value={`+${formatCurrency(result.advantageAmount)}`}
          color={result.winner === 'roth' ? colors.positive : colors.secondary}
        />
      </div>
      <div className={`grid gap-3 ${result.annualEmployerMatch > 0 ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-3'}`}>
        <ResultCard label="Annual Tax Savings (Trad)" value={formatCurrency(result.annualTaxSavings)} color={colors.warning} />
        <ResultCard label="Break-Even Retirement Rate" value={result.breakEvenRate != null ? `${result.breakEvenRate}%` : 'N/A'} color={colors.chartTick} />
        <ResultCard label="Retirement Effective Rate" value={`${result.retirementEffectiveRate}%`} color={colors.negative} />
        {result.annualEmployerMatch > 0 && (
          <ResultCard label={`Employer Match (${result.employerMatchRate}%)`} value={formatCurrency(result.annualEmployerMatch) + '/yr'} color={colors.positive} />
        )}
      </div>

      {/* Employer Match Note */}
      {result.annualEmployerMatch > 0 && (
        <GlassCard className="!py-3 !px-4">
          <p className="text-xs text-body">
            <span className="font-semibold text-heading">Employer match note:</span> Your employer contributes {formatCurrency(result.annualEmployerMatch)}/yr ({result.employerMatchRate}% of salary) on top of your {formatCurrency(annualContribution)}/yr. This match is always pre-tax — even with a Roth 401(k), the employer portion goes into a Traditional pre-tax account and is taxed at withdrawal. Both projections above include the match.
          </p>
        </GlassCard>
      )}

      {/* Projection Chart */}
      {result.yearlyProjection.length > 0 && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Growth Projection</h3>
          <p className="text-xs text-body mb-4">Roth balance (tax-free) vs Traditional total (pre-tax account + reinvested tax savings)</p>
          <div ref={projectionChartRef} className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={result.yearlyProjection}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                <XAxis
                  dataKey="age"
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  tickFormatter={v => `Age ${v}`}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  tickFormatter={v => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(12px)',
                    color: 'var(--heading)',
                  }}
                  formatter={(v, name) => [formatCurrency(v), name]}
                  labelFormatter={v => `Age ${v}`}
                />
                <Legend />
                <Area type="monotone" dataKey="rothAfterTax" name="Roth (After-Tax)" stroke={colors.positive} fill={colors.positive} fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="tradAfterTax" name="Traditional (After-Tax)" stroke={colors.secondary} fill={colors.secondary} fillOpacity={0.15} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      )}

      {/* Break-Even Chart */}
      {result.breakEvenData.length > 0 && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Break-Even Analysis</h3>
          <p className="text-xs text-body mb-4">After-tax value at different retirement tax rates. Where Roth crosses Traditional is your break-even point.</p>
          <div ref={breakEvenChartRef} className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={result.breakEvenData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  tickFormatter={v => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(12px)',
                    color: 'var(--heading)',
                  }}
                  formatter={(v, name) => [formatCurrency(v), name]}
                  labelFormatter={v => `Retirement Rate: ${v}`}
                />
                <Legend />
                {result.breakEvenRate != null && (
                  <ReferenceLine
                    x={`${Math.round(result.breakEvenRate / 5) * 5}%`}
                    stroke={colors.warning}
                    strokeDasharray="5 5"
                    label={{ value: 'Break-even', fill: colors.warning, fontSize: 11 }}
                  />
                )}
                <Bar dataKey="rothAfterTax" name="Roth" fill={colors.positive} radius={[4, 4, 0, 0]} />
                <Bar dataKey="tradAfterTax" name="Traditional" fill={colors.secondary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      )}

      {/* Contribution Limits Reference */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-3">2025 Contribution Limits</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <LimitCard label="401(k)" value={formatCurrency(CONTRIBUTION_LIMITS_2025.traditional401k)} sub={`${formatCurrency(CONTRIBUTION_LIMITS_2025.traditional401kCatchUp)} if 50+`} />
          <LimitCard label="Roth IRA" value={formatCurrency(CONTRIBUTION_LIMITS_2025.rothIRA)} sub={`${formatCurrency(CONTRIBUTION_LIMITS_2025.rothIRACatchUp)} if 50+`} />
          <LimitCard label="HSA (Individual)" value={formatCurrency(CONTRIBUTION_LIMITS_2025.hsaIndividual)} />
          <LimitCard label="HSA (Family)" value={formatCurrency(CONTRIBUTION_LIMITS_2025.hsaFamily)} />
          <div className="p-2.5 rounded-xl bg-subtle text-center">
            <p className="text-xs text-body">Roth IRA Eligibility</p>
            <Badge
              variant={result.rothIRAEligibility.eligible ? 'success' : 'danger'}
              className="mt-1"
            >
              {result.rothIRAEligibility.status === 'full' ? 'Fully Eligible' :
               result.rothIRAEligibility.status === 'partial' ? `Partial (${formatCurrency(result.rothIRAEligibility.maxContribution)})` :
               'Phased Out'}
            </Badge>
            {result.rothIRAEligibility.note && (
              <p className="text-xs text-body mt-1">{result.rothIRAEligibility.note}</p>
            )}
          </div>
        </div>
        <div className="mt-3 text-xs text-body">
          Roth IRA MAGI Phase-out: {formatCurrency(ROTH_IRA_PHASEOUT[filingStatus].start)} - {formatCurrency(ROTH_IRA_PHASEOUT[filingStatus].end)} ({FILING_STATUSES.find(f => f.key === filingStatus)?.label})
          {' | '}Standard Deduction: {formatCurrency(STANDARD_DEDUCTIONS[filingStatus])}
        </div>
      </GlassCard>

      {/* Projection Table (Collapsible) */}
      <GlassCard className="!p-0 overflow-hidden">
        <button
          onClick={() => setShowProjectionTable(!showProjectionTable)}
          className="w-full flex items-center justify-between px-6 py-4 text-sm font-semibold text-body hover:bg-subtle transition"
        >
          <span>Year-by-Year Projection</span>
          <svg className={`w-4 h-4 text-body transition-transform ${showProjectionTable ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showProjectionTable && (
          <div className="overflow-x-auto border-t border-card">
            <table className="w-full">
              <thead>
                <tr className="border-b border-card">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-body">Year</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-body">Age</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-body">Roth Balance</th>
                  {result.annualEmployerMatch > 0 && (
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-body">Match (Pre-Tax)</th>
                  )}
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-body">Trad Pre-Tax</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-body">Trad Brokerage</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-body">Roth After-Tax</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-body">Trad After-Tax</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-body">Advantage</th>
                </tr>
              </thead>
              <tbody>
                {result.yearlyProjection.map((r) => (
                  <tr
                    key={r.year}
                    className={`border-b border-subtle hover:bg-subtle transition text-sm ${
                      r.year % 5 === 0 ? 'bg-subtle/50' : ''
                    }`}
                  >
                    <td className="px-4 py-2 text-body">{r.year}</td>
                    <td className="px-4 py-2 text-right text-body">{r.age}</td>
                    <td className="px-4 py-2 text-right text-heading">{formatCurrency(r.rothBalance)}</td>
                    {result.annualEmployerMatch > 0 && (
                      <td className="px-4 py-2 text-right text-positive">{formatCurrency(r.rothEmployerBalance)}</td>
                    )}
                    <td className="px-4 py-2 text-right text-body">{formatCurrency(r.tradPreTaxBalance)}</td>
                    <td className="px-4 py-2 text-right text-body">{formatCurrency(r.tradBrokerageBalance)}</td>
                    <td className="px-4 py-2 text-right font-medium" style={{ color: colors.positive }}>{formatCurrency(r.rothAfterTax)}</td>
                    <td className="px-4 py-2 text-right font-medium" style={{ color: colors.secondary }}>{formatCurrency(r.tradAfterTax)}</td>
                    <td className="px-4 py-2 text-right">
                      <span className={r.advantage >= 0 ? 'text-positive' : 'text-warn'}>
                        {r.advantage >= 0 ? '+' : ''}{formatCurrency(r.advantage)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
    </div>
  )
}

function getBracketColor(rate) {
  const bracketColors = {
    10: '#16a34a',
    12: '#65a30d',
    22: '#ca8a04',
    24: '#ea580c',
    32: '#dc2626',
    35: '#be123c',
    37: '#9f1239',
  }
  return bracketColors[rate] || '#6b7280'
}

function InputField({ label, value, onChange, prefix, suffix, type = 'text', step, min, max, hint }) {
  return (
    <div>
      {label && <label className="block text-xs font-medium text-body mb-1.5">{label}</label>}
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted">{prefix}</span>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          step={step}
          min={min}
          max={max}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 ${
            prefix && suffix ? 'pl-7 pr-16' : prefix ? 'pl-7 pr-3' : suffix ? 'pl-3 pr-16' : 'px-3'
          }`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted">{suffix}</span>
        )}
      </div>
      {hint && <p className="text-xs text-muted mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-body mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

function LimitCard({ label, value, sub }) {
  return (
    <div className="p-2.5 rounded-xl bg-subtle text-center">
      <p className="text-xs text-body">{label}</p>
      <p className="text-sm font-bold text-heading mt-0.5">{value}</p>
      {sub && <p className="text-xs text-muted">{sub}</p>}
    </div>
  )
}
